'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DrawerMenu from './DrawerMenu.react';
import { useMealOperations, useMealPlanData, usePermissions } from '../contexts';

import '../../../Dashboard/SwapMenu.scss';

/**
 * SwapMenu - Functional component for Plans/Editor
 * Uses DrawerMenu for swap options
 */
const SwapMenu = ({ 
    primary, 
    onClickDetails,
    onClickRefresh,
    loading,
    canUndoRefresh,
    onUndoRefresh,
    meals,
    button,
    className 
}) => {
    const { startReplaceMeal } = useMealOperations();
    const { user } = usePermissions();
    const { inhibit_add_swap_modes = [] } = user?.preferences || {};

    // Define available swap modes
    const addSwapModes = [
        'smart-choices',
        'restaurants', 
        'favorites',
        'ingredients',
        'browser',
        'use-own-recipe',
        'create-custom',
    ];
    
    const allowedModes = addSwapModes.filter(mode => !inhibit_add_swap_modes.includes(mode));

    // Check if there's only one swap option (auto-select)
    const checkForOnlyOneSwapOption = () => {
        if (allowedModes.length === 1) {
            startReplaceMeal(primary.offset, primary.meal, meals, { 
                defaultMode: allowedModes[0],
                allowedModes 
            });
            return true;
        }
        return false;
    };

    return (
        <DrawerMenu
            button={button || <span>swap</span>}
            className={classNames('swap-menu-btn', className)}
            modalClassName="feed-swap-menu"
            menuTitle="TRY THESE OPTIONS"
            checkForOnlyOneSwapOption={checkForOnlyOneSwapOption}
        >
            {({ closeModal }) => (
                <div className="feed-menu-content">
                    {allowedModes.includes('smart-choices') && (
                        <button
                            className="menu-item smart-choices"
                            onClick={() => {
                                startReplaceMeal(primary.offset, primary.meal, meals, { defaultMode: 'smart-choices', allowedModes });
                                closeModal();
                            }}
                        >
                            Smart Choices <i className="icon-lightbulb" />
                        </button>
                    )}
                    {allowedModes.includes('restaurants') && (
                        <button
                            className="menu-item eat-out-instead"
                            onClick={() => {
                                startReplaceMeal(primary.offset, primary.meal, meals, { defaultMode: 'restaurants', allowedModes });
                                closeModal();
                            }}
                        >
                            Restaurant Meal <i className="icon-restaurant-meals" />
                        </button>
                    )}
                    {allowedModes.includes('favorites') && (
                        <button
                            className="menu-item favorites"
                            onClick={() => {
                                startReplaceMeal(primary.offset, primary.meal, meals, { defaultMode: 'favorites', allowedModes });
                                closeModal();
                            }}
                        >
                            Choose from favorites <i className="icon-heart2" />
                        </button>
                    )}
                    {allowedModes.includes('ingredients') && (
                        <button
                            className="menu-item search-by-ingredients"
                            onClick={() => {
                                startReplaceMeal(primary.offset, primary.meal, meals, { defaultMode: 'ingredients', allowedModes });
                                closeModal();
                            }}
                        >
                            Search by Ingredients <i className="icon-list1" />
                        </button>
                    )}
                    {allowedModes.includes('browser') && (
                        <button
                            className="menu-item search"
                            onClick={() => {
                                startReplaceMeal(primary.offset, primary.meal, meals, { defaultMode: 'browser', allowedModes });
                                closeModal();
                            }}
                        >
                            Search Foods and Recipes <i className="icon-search5" />
                        </button>
                    )}
                    {allowedModes.includes('use-own-recipe') && (
                        <button
                            className="menu-item search"
                            onClick={() => {
                                startReplaceMeal(primary.offset, primary.meal, meals, { defaultMode: 'use-own-recipe', allowedModes });
                                closeModal();
                            }}
                        >
                            Use My Own Recipe <i className="icon-add-circle-outline2" />
                        </button>
                    )}
                    {allowedModes.includes('create-custom') && (
                        <button
                            className="menu-item search"
                            onClick={() => {
                                startReplaceMeal(primary.offset, primary.meal, meals, { defaultMode: 'create-custom', allowedModes });
                                closeModal();
                            }}
                        >
                            Create Custom Food <i className="icon-paper" />
                        </button>
                    )}
                </div>
            )}
        </DrawerMenu>
    );
};

SwapMenu.propTypes = {
    primary: PropTypes.object,
    meals: PropTypes.array.isRequired,
    onClickDetails: PropTypes.func,
    onClickRefresh: PropTypes.func,
    loading: PropTypes.bool,
    canUndoRefresh: PropTypes.bool,
    onUndoRefresh: PropTypes.func,
    button: PropTypes.node,
    className: PropTypes.string,
};

export default SwapMenu;